import { Editor, escapeForRegEx } from '@tiptap/core';

const triggerInsertContent = (precedingChar: string, trigger: string): string => {
	return precedingChar.trim() !== '' ? ` ${trigger}` : trigger;
};

export const insertTrigger = (editor: Editor, trigger: string): void => {
	const { from } = editor.state.selection;
	const precedingChar = editor.state.doc.textBetween(Math.max(0, from - 1), from);
	editor.chain().focus().insertContent(triggerInsertContent(precedingChar, trigger)).run();
};

/** Replace a tag chip with its bare trigger and focus after it. Drops the prior value so the list reopens unfiltered; undo restores the chip. */
export const reopenTrigger = (editor: Editor, nodeName: string, trigger: string): boolean => {
	let found: { pos: number; size: number } | undefined = undefined;

	editor.state.doc.descendants((node, pos) => {
		if (found) {
			return false;
		}

		if (node.type.name === nodeName) {
			found = { pos, size: node.nodeSize };
			return false;
		}
	});

	if (!found) {
		return false;
	}

	const { pos, size } = found;

	editor.commands.command(({ tr }) => {
		tr.insertText(trigger, pos, pos + size);
		return true;
	});

	// Toolbar clicks may not move DOM focus into the editor.
	editor.commands.focus(pos + trigger.length);

	return true;
};

/** Focus after an existing bare trigger (e.g. `+` / `+25`) so the suggestion reopens without inserting another. `querySource` is the matcher `.source`. */
export const focusExistingTrigger = (editor: Editor, trigger: string, querySource = ''): boolean => {
	const pattern = new RegExp(`(?<!\\w)${escapeForRegEx(trigger)}${querySource}`);
	let focusPos: number | undefined;

	editor.state.doc.descendants((node, pos) => {
		if (focusPos !== undefined) {
			return false;
		}

		if (!node.isText || !node.text) {
			return;
		}

		const match = node.text.match(pattern);
		if (!match || match.index === undefined) {
			return;
		}

		// End of the full match (trigger + any in-progress query, e.g. "+25") so
		// the suggestion range covers all of it and selection replaces the whole
		// match instead of leaving trailing digits behind.
		focusPos = pos + match.index + match[0].length;
		return false;
	});

	if (focusPos === undefined) {
		return false;
	}

	if (editor.state.selection.from === focusPos) {
		// Re-run suggestion apply without moving the caret (e.g. after Escape).
		editor.view.dispatch(editor.state.tr.setMeta('addToHistory', false));
		// Restore DOM focus stolen by the toolbar click; view.focus() does not
		// dispatch, so it cannot disturb the re-check above.
		editor.view.focus();
	} else {
		editor.commands.focus(focusPos);
	}

	return true;
};
